import { Fragment } from 'react'
import { useForm } from '@inertiajs/react'

import { TextInput } from '@atoms'
import { RichTextEditorField } from '@components/molecules/RichTextEditorField'
import { scopedTranslation, t as translate } from '@utils/I18n'

import { fieldErrors, SettingsForm, textField } from './SettingsForm'

export type EmailTemplate = {
  id: string
  keys: string[]
  richTextKey: string
  variables: Record<string, string>
}

export type EmailTemplateFormProps = {
  action: string
  values: Record<string, string>
  template: EmailTemplate
  t: ReturnType<typeof scopedTranslation>
}

const SHARED_LABEL = /_(SUBJECT|CONTENT)$/

const fieldLabel = (key: string, t: EmailTemplateFormProps['t']) => {
  const shared = key.match(SHARED_LABEL)?.[1]
  return shared ? translate(`configurations.fields.${shared.toLowerCase()}`) : t(`fields.${key}`)
}

function Variables({ variables, t }: Pick<EmailTemplateFormProps, 't'> & { variables: Record<string, string> }) {
  return (
    <div className="tw:rounded-lg tw:bg-gray-cool-50 tw:p-4 tw:text-sm tw:text-gray-cool-700">
      <p className="tw:m-0 tw:font-medium tw:text-gray-cool-900">{translate('configurations.variables_available')}</p>
      <ul className="tw:m-0 tw:mt-2 tw:list-none tw:space-y-1 tw:p-0">
        {Object.entries(variables).map(([name, tag]) => (
          <li key={name}>
            {t(`variables.${name}`)}: <code>{tag}</code>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function EmailTemplateForm({ action, values, template, t }: EmailTemplateFormProps) {
  const form = useForm(Object.fromEntries(template.keys.map((key) => [key, values[key]])))
  const hasVariables = Object.keys(template.variables).length > 0

  return (
    <SettingsForm form={form} action={action}>
      {template.keys.map((key) =>
        key === template.richTextKey ? (
          <Fragment key={key}>
            {hasVariables && <Variables variables={template.variables} t={t} />}
            <RichTextEditorField
              name={key}
              label={fieldLabel(key, t)}
              value={form.data[key]}
              onChange={(value) => form.setData(key, value)}
              errors={fieldErrors(form.errors)}
            />
          </Fragment>
        ) : (
          <TextInput
            key={key}
            label={fieldLabel(key, t)}
            {...(key.endsWith('_EMAIL') && { type: 'email' })}
            {...textField(form, key)}
          />
        )
      )}
    </SettingsForm>
  )
}
